import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import useGallery from '../../GalleryConfiguration/components/hooks/useGallery';
import useSurveyQuestions from '../../GalleryConfiguration/components/hooks/useSurveyQuestions';
import useTemplates, {
    EmailTemplate,
} from '../../EmailTemplates/components/hooks/useTemplates';
import EventShell from '../chrome/EventShell';
import useEventChrome from '../chrome/useEventChrome';
import {
    CheckRow,
    Field,
    SaveBar,
    SectionCard,
    Select,
    Textarea,
    TextInput,
    Toggle,
    ToggleRow,
} from '../components/forms';
import './event/delivery.css';

/*
 * Sharing & Delivery — ONE config page (redline 2026-09-22: flipping a
 * step must not require navigation). The journey is edited in place,
 * cards in rule-21 order. Rule 51, refined: a step's on/off sits in
 * its card HEADER and acts immediately (whole-object save, rule 24,
 * optimistic with rollback); a card's BODY fields batch behind the one
 * save bar. Shares · Sessions · Reengagement are the area's tools —
 * the only sub-pages (they bridge to the live v2 surfaces for now).
 */

const BATCHED: Array<{ key: string; section: string }> = [
    { key: 'verifiedAge', section: 'Age gate' },
    { key: 'disclaimer', section: 'Disclaimer' },
    { key: 'disclaimerRequired', section: 'Disclaimer' },
    { key: 'disclaimerOptOut', section: 'Disclaimer' },
    { key: 'preShareSurveyUrl', section: 'Pre-share survey' },
    { key: 'surveyRequired', section: 'Pre-share survey' },
    { key: 'surveyUrl', section: 'Post-share survey' },
    { key: 'fromName', section: 'Email' },
    { key: 'fromAddress', section: 'Email' },
    { key: 'subject', section: 'Email' },
    { key: 'emailBody', section: 'Email' },
    { key: 'textBody', section: 'SMS' },
    { key: 'sendAttachments', section: 'Delivery behavior' },
    { key: 'sendLinkToPhoto', section: 'Delivery behavior' },
    { key: 'showGalleryLink', section: 'Delivery behavior' },
    { key: 'shareLinkTarget', section: 'Delivery behavior' },
];

const Chevron = () => (
    <svg className="chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="m9 6 6 6-6 6" />
    </svg>
);

/* internal pre-share question, as v2 sends it in surveyQuestions */
interface SurveyQ {
    id?: number;
    question: string;
    answerTypeId: number;
    isDeleted: boolean;
}

const toQuestions = (rows: any): SurveyQ[] =>
    (Array.isArray(rows) ? rows : []).map((q: any) => ({
        id: q.id,
        question: q.question ?? '',
        answerTypeId: q.answerTypeId ?? 2,
        isDeleted: !!q.isDeleted,
    }));

/** keeps /sharing/methods bookmarks working after the fold-in */
export function ShareMethodsRedirect() {
    const { activationId } = useParams();
    return <Navigate to={`/activations/${activationId}/sharing`} replace />;
}

export default function SharingV3() {
    const { activationId } = useParams();
    const id = Number(activationId);
    const navigate = useNavigate();
    const chrome = useEventChrome(id);
    const { getGallery, saveGallery } = useGallery();
    const { getSurveyQuestions } = useSurveyQuestions();
    const { getTemplates } = useTemplates();

    const [gallery, setGallery] = useState<any>(null);
    /* saved questions + the batched draft of them */
    const [savedQuestions, setSavedQuestions] = useState<SurveyQ[]>([]);
    const [questions, setQuestions] = useState<SurveyQ[]>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [draft, setDraft] = useState<Record<string, any>>({});
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!chrome.galleryId) return;
        let alive = true;
        getGallery(chrome.galleryId)
            .then((g) => {
                if (alive) setGallery(g);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chrome.galleryId]);

    useEffect(() => {
        if (!id) return;
        let alive = true;
        getSurveyQuestions(id)
            .then((rows) => {
                if (!alive) return;
                const q = toQuestions(rows);
                setSavedQuestions(q);
                setQuestions(q);
            })
            .catch(() => {});
        getTemplates()
            .then((rows) => {
                if (alive) setTemplates(Array.isArray(rows) ? rows : []);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const questionCount = savedQuestions.filter((q) => !q.isDeleted).length;
    const questionsDirty =
        JSON.stringify(questions) !== JSON.stringify(savedQuestions);
    const editQuestion = (i: number, patch: Partial<SurveyQ>) =>
        setQuestions((qs) =>
            qs.map((q, j) => (j === i ? { ...q, ...patch } : q)),
        );
    const shownNo = (i: number) =>
        questions.slice(0, i + 1).filter((q) => !q.isDeleted).length;
    /* saved rows soft-delete (server needs the id); new rows just drop */
    const removeQuestion = (i: number) =>
        setQuestions((qs) =>
            qs[i]?.id != null
                ? qs.map((q, j) => (j === i ? { ...q, isDeleted: true } : q))
                : qs.filter((_, j) => j !== i),
        );

    const value = (key: string) =>
        key in draft ? draft[key] : (gallery?.[key] ?? '');
    const edit = (key: string, v: any) =>
        setDraft((d) => ({ ...d, [key]: v }));

    const dirtySections = useMemo(() => {
        const sections: string[] = [];
        BATCHED.forEach(({ key, section }) => {
            if (
                key in draft &&
                draft[key] !== (gallery?.[key] ?? '') &&
                !sections.includes(section)
            ) {
                sections.push(section);
            }
        });
        if (questionsDirty && !sections.includes('Pre-share survey'))
            sections.push('Pre-share survey');
        return sections;
    }, [draft, gallery, questionsDirty]);

    /* header/on-off semantic: immediate whole-object write, optimistic,
       rolled back with an honest line on failure */
    const flip = async (key: string, on: boolean) => {
        if (!gallery || !chrome.galleryId) return;
        const before = gallery;
        const next = { ...gallery, [key]: on };
        setGallery(next);
        setFailed(false);
        const ok = await saveGallery(chrome.galleryId, next).catch(
            () => false,
        );
        if (!ok) {
            setGallery(before);
            setFailed(true);
        }
    };

    const save = async () => {
        if (!gallery || !chrome.galleryId || saving) return;
        setSaving(true);
        setFailed(false);
        /* same surveyQuestions payload as v2: rows without an id are
           inserted, isDeleted rows removed, the rest updated */
        const next = {
            ...gallery,
            ...draft,
            surveyQuestions: questions.filter(
                (q) => q.id != null || (!q.isDeleted && q.question.trim()),
            ),
        };
        const ok = await saveGallery(chrome.galleryId, next).catch(
            () => false,
        );
        if (ok) {
            /* re-fetch so new rows carry server ids — a later whole-object
               save must not insert them again */
            const fresh = await getSurveyQuestions(id)
                .then(toQuestions)
                .catch(() =>
                    next.surveyQuestions.filter(
                        (q: SurveyQ) => q.id != null && !q.isDeleted,
                    ),
                );
            setGallery({ ...next, surveyQuestions: fresh });
            setSavedQuestions(fresh);
            setQuestions(fresh);
            setDraft({});
        } else {
            setFailed(true);
        }
        setSaving(false);
    };

    const headerToggle = (key: string) => (
        <Toggle
            label=""
            checked={!!gallery?.[key]}
            onChange={(e) => flip(key, e.target.checked)}
        />
    );

    const methodsOn =
        !!gallery && (gallery.emailEnabled || gallery.textEnabled);
    const methodsState = !gallery
        ? ''
        : methodsOn
          ? [
                gallery.emailEnabled && 'Email',
                gallery.textEnabled && 'SMS',
            ]
                .filter(Boolean)
                .join(' + ') + ' on'
          : 'Sharing disabled — nothing sends until email or SMS is on';

    const surveyNote = (url: string) =>
        url
            ? 'External URL'
            : questionCount > 0
              ? `Internal · ${questionCount} question${questionCount === 1 ? '' : 's'}`
              : 'Off';

    const toolRow = (label: string, to: string) => (
        <div
            className="trow2 push"
            role="link"
            tabIndex={0}
            onClick={() => navigate(to)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(to);
            }}
        >
            <span className="nm2">{label}</span>
            <span className="right">
                <Chevron />
            </span>
        </div>
    );

    return (
        <EventShell
            orgInitials={chrome.orgInitials}
            orgLogo={chrome.orgLogo}
            eventName={chrome.eventName}
            eventSub={chrome.eventSub}
            live={chrome.live}
            quickLinks={chrome.quickLinks}
            nav={chrome.nav}
            activeSection="sharing"
        >
            <div className="dets">
                <h1 className="pg-title">Sharing &amp; Delivery</h1>
                <p className="pg-sub">
                    {methodsState || ' '}
                    {failed
                        ? ' · last change didn’t save — try again'
                        : ''}
                </p>

                {gallery === null ? (
                    <p className="quiet-empty">&nbsp;</p>
                ) : (
                    <>
                        <div className="stack">
                            <SectionCard
                                title="1 · Age gate"
                                action={headerToggle('ageVerification')}
                            >
                                <Field label="Minimum age">
                                    <TextInput
                                        type="number"
                                        style={{ maxWidth: 120 }}
                                        value={value('verifiedAge')}
                                        onChange={(e) =>
                                            edit(
                                                'verifiedAge',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </Field>
                            </SectionCard>

                            <SectionCard
                                title="2 · Disclaimer"
                                action={headerToggle('disclaimerEnabled')}
                            >
                                <div className="field wide">
                                    <span className="field-l">Text</span>
                                    <Textarea
                                        value={value('disclaimer')}
                                        onChange={(e) =>
                                            edit(
                                                'disclaimer',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                                <CheckRow
                                    label="Require agreement"
                                    checked={!!value('disclaimerRequired')}
                                    onChange={(on) =>
                                        edit('disclaimerRequired', on)
                                    }
                                />
                                <CheckRow
                                    label="Offer opt-out"
                                    checked={!!value('disclaimerOptOut')}
                                    onChange={(on) =>
                                        edit('disclaimerOptOut', on)
                                    }
                                />
                            </SectionCard>

                            <SectionCard
                                title="3 · Pre-share survey"
                                note={surveyNote(
                                    value('preShareSurveyUrl'),
                                )}
                            >
                                <div className="field wide">
                                    <span className="field-l">
                                        Built-in questions
                                    </span>
                                    {questions.map((q, i) =>
                                        q.isDeleted ? null : (
                                            <div
                                                className="dl-q"
                                                key={q.id ?? `new-${i}`}
                                            >
                                                <TextInput
                                                    aria-label={`Question ${shownNo(i)}`}
                                                    placeholder="Question"
                                                    value={q.question}
                                                    onChange={(e) =>
                                                        editQuestion(i, {
                                                            question:
                                                                e.target.value,
                                                        })
                                                    }
                                                />
                                                <Select
                                                    aria-label={`Answer type for question ${shownNo(i)}`}
                                                    value={q.answerTypeId}
                                                    onChange={(e) =>
                                                        editQuestion(i, {
                                                            answerTypeId:
                                                                +e.target
                                                                    .value,
                                                        })
                                                    }
                                                >
                                                    <option value={2}>
                                                        Text
                                                    </option>
                                                    <option value={1}>
                                                        Yes/No
                                                    </option>
                                                </Select>
                                                <button
                                                    type="button"
                                                    className="tb-btn ghost dl-q-x"
                                                    aria-label={`Remove question ${shownNo(i)}`}
                                                    onClick={() =>
                                                        removeQuestion(i)
                                                    }
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ),
                                    )}
                                    <div className="dl-row ev-gap">
                                        <button
                                            type="button"
                                            className="tb-btn"
                                            onClick={() =>
                                                setQuestions((qs) => [
                                                    ...qs,
                                                    {
                                                        question: '',
                                                        answerTypeId: 2,
                                                        isDeleted: false,
                                                    },
                                                ])
                                            }
                                        >
                                            Add question
                                        </button>
                                    </div>
                                </div>
                                <CheckRow
                                    label="Require answers"
                                    checked={!!value('surveyRequired')}
                                    onChange={(on) =>
                                        edit('surveyRequired', on)
                                    }
                                />
                                <div className="field wide">
                                    <span className="field-l">
                                        External URL
                                    </span>
                                    <TextInput
                                        type="url"
                                        placeholder="Optional — https://…"
                                        value={value('preShareSurveyUrl')}
                                        onChange={(e) =>
                                            edit(
                                                'preShareSurveyUrl',
                                                e.target.value,
                                            )
                                        }
                                    />
                                    <span className="field-h">
                                        For client-specific surveys; works
                                        alongside the built-in questions.
                                    </span>
                                </div>
                            </SectionCard>

                            <SectionCard
                                title="4 · Post-share survey"
                                note={surveyNote(value('surveyUrl'))}
                            >
                                <div className="field wide">
                                    <span className="field-l">
                                        External URL
                                    </span>
                                    <TextInput
                                        type="url"
                                        placeholder="https://…"
                                        value={value('surveyUrl')}
                                        onChange={(e) =>
                                            edit(
                                                'surveyUrl',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                            </SectionCard>

                            <SectionCard
                                title="5 · Share methods"
                                note="take effect immediately"
                            >
                                <ToggleRow
                                    label="Email"
                                    sub="delivers the guest's photos by email"
                                    checked={!!gallery.emailEnabled}
                                    onChange={(on) =>
                                        flip('emailEnabled', on)
                                    }
                                />
                                <ToggleRow
                                    label="SMS"
                                    sub="delivers by text message"
                                    checked={!!gallery.textEnabled}
                                    onChange={(on) =>
                                        flip('textEnabled', on)
                                    }
                                />
                            </SectionCard>

                            <SectionCard title="Email">
                                <Field label="From name">
                                    <TextInput
                                        value={value('fromName')}
                                        onChange={(e) =>
                                            edit(
                                                'fromName',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </Field>
                                <Field label="From address">
                                    <TextInput
                                        type="email"
                                        value={value('fromAddress')}
                                        onChange={(e) =>
                                            edit(
                                                'fromAddress',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </Field>
                                <div className="field wide">
                                    <span className="field-l">Subject</span>
                                    <TextInput
                                        value={value('subject')}
                                        onChange={(e) =>
                                            edit('subject', e.target.value)
                                        }
                                    />
                                </div>
                                {templates.length > 0 && (
                                    <div className="field wide">
                                        <span className="field-l">
                                            Apply email template
                                        </span>
                                        <Select
                                            aria-label="Apply email template"
                                            value=""
                                            onChange={(e) => {
                                                const t = templates.find(
                                                    (x) =>
                                                        String(x.id) ===
                                                        e.target.value,
                                                );
                                                if (t)
                                                    edit('emailBody', t.body);
                                            }}
                                        >
                                            <option value="">
                                                Choose a template…
                                            </option>
                                            {templates.map((t) => (
                                                <option
                                                    key={t.id}
                                                    value={String(t.id)}
                                                >
                                                    {t.name}
                                                </option>
                                            ))}
                                        </Select>
                                        <span className="field-h">
                                            Replaces the body below; discard
                                            to undo.
                                        </span>
                                    </div>
                                )}
                                <div className="field wide">
                                    <span className="field-l">Body</span>
                                    <Textarea
                                        value={value('emailBody')}
                                        onChange={(e) =>
                                            edit(
                                                'emailBody',
                                                e.target.value,
                                            )
                                        }
                                    />
                                </div>
                            </SectionCard>

                            <SectionCard title="SMS">
                                <div className="field wide">
                                    <span className="field-l">Message</span>
                                    <Textarea
                                        value={value('textBody')}
                                        onChange={(e) =>
                                            edit('textBody', e.target.value)
                                        }
                                    />
                                    <span className="field-h">
                                        The guest's own gallery link rides
                                        the delivery.
                                    </span>
                                </div>
                            </SectionCard>

                            <SectionCard title="Delivery behavior">
                                <CheckRow
                                    label="Send photos as attachments"
                                    checked={!!value('sendAttachments')}
                                    onChange={(on) =>
                                        edit('sendAttachments', on)
                                    }
                                />
                                <CheckRow
                                    label="Link to the shared photo"
                                    checked={!!value('sendLinkToPhoto')}
                                    onChange={(on) =>
                                        edit('sendLinkToPhoto', on)
                                    }
                                />
                                <CheckRow
                                    label="Show gallery link in delivery"
                                    checked={!!value('showGalleryLink')}
                                    onChange={(on) =>
                                        edit('showGalleryLink', on)
                                    }
                                />
                                <div
                                    className="field"
                                    style={{ marginTop: 12 }}
                                >
                                    <span className="field-l">
                                        Share link opens
                                    </span>
                                    <Select
                                        value={
                                            value('shareLinkTarget') ||
                                            'activationGallery'
                                        }
                                        onChange={(e) =>
                                            edit(
                                                'shareLinkTarget',
                                                e.target.value,
                                            )
                                        }
                                    >
                                        <option value="activationGallery">
                                            Event gallery
                                        </option>
                                        <option value="sessionGallery">
                                            Session gallery
                                        </option>
                                        <option value="personalGallery">
                                            Personal gallery
                                        </option>
                                        <option value="jobGallery">
                                            Job gallery
                                        </option>
                                    </Select>
                                </div>
                            </SectionCard>

                            <SectionCard title="Tools">
                                {toolRow(
                                    'Shares',
                                    `/activations/${id}/shares`,
                                )}
                                {toolRow(
                                    'Sessions',
                                    `/activations/${id}/insights`,
                                )}
                                {toolRow(
                                    'Delivery · retry, reengagement, webhook',
                                    `/activations/${id}/delivery`,
                                )}
                            </SectionCard>
                        </div>

                        <SaveBar
                            dirty={dirtySections}
                            saving={saving}
                            onSave={save}
                            onDiscard={() => {
                                setDraft({});
                                setQuestions(savedQuestions);
                            }}
                        />
                    </>
                )}
            </div>
        </EventShell>
    );
}
